/**
 * 视频理赔（新版）：全局呼入弹窗
 * 客户发起视频呼叫后，坐席端出现"视频呼入"浮动窗口：
 * - 浮动可拖拽，不遮挡页面其他区域点击
 * - 显示：被邀请方姓名 + 类型 + 受理号
 * - 操作：接听 / 拒接
 * - 超时：自动调用 callingTimeout 接口并关闭
 *
 * 与 server 端 socket 事件对接：
 *   socket.on('videoClaim:calling')  → 打开弹窗（window postMessage）
 *   socket.on('videoClaim:callingClosed') → 关闭弹窗（window postMessage）
 */
import React, { Component } from 'react';
import { Button, Spin, message } from 'antd';
import { withRouter } from 'react-router-dom';
import Draggable from 'react-draggable';
import {
    answerVideoClaim,
    rejectVideoClaim,
    markVideoClaimCallingTimeout,
} from '@service/videoClaim';
import './index.css';

const INVITEE_TYPE_LABEL = {
    OWN_VEHICLE: '标的',     // 标的（被保险人/车主）
    THIRD_PARTY: '三者',     // 三者（三者1..n 对应人/车）
    OTHER: '其他',                 // 其他（手动输入手机号）
};

class VideoClaimIncoming extends Component {
    constructor(props) {
        super(props);
        this.state = {
            visible: false,
            incoming: null,
            countdown: 0,
            acting: false,
            ringAudio: null,
        };
        this._countdownTimer = null;
        this._onWindowMessage = this._onWindowMessage.bind(this);
    }

    componentDidMount() {
        window.addEventListener('message', this._onWindowMessage);
    }

    componentWillUnmount() {
        window.removeEventListener('message', this._onWindowMessage);
        this.clearTimers();
        this.stopRing();
    }

    clearTimers() {
        if (this._countdownTimer) {
            clearInterval(this._countdownTimer);
            this._countdownTimer = null;
        }
    }

    playRing() {
        this.stopRing();
        try {
            const audio = new Audio('/audio/notification.mp3');
            audio.loop = true;
            audio.play && audio.play().catch(() => {});
            this.setState({ ringAudio: audio });
        } catch (e) {}
    }

    stopRing() {
        try {
            const audio = this.state.ringAudio;
            if (audio) {
                audio.pause && audio.pause();
                audio.loop = false;
            }
        } catch (e) {}
        if (this.state.ringAudio) this.setState({ ringAudio: null });
    }

    _onWindowMessage(event) {
        const data = (event && event.data) || {};
        if (!data || !data.security) return;
        if (data.eventName === 'VideoClaimIncoming') {
            const incoming = data.socketMessage || {};
            if (!incoming || !incoming.sessionId) return;
            const timeout = Math.max(60 * 1000, Number(incoming.callingTimeoutMs) || 60 * 1000);
            this.clearTimers();
            const countdown = Math.ceil(timeout / 1000);
            this.setState({
                visible: true,
                incoming,
                countdown,
            });
            this.playRing();
            this._countdownTimer = setInterval(() => {
                this.setState((prev) => {
                    const next = (prev.countdown || 0) - 1;
                    if (next <= 0) {
                        this.handleCallingTimeout(true);
                        return { countdown: 0 };
                    }
                    return { countdown: next };
                });
            }, 1000);
        } else if (data.eventName === 'VideoClaimIncomingClosed') {
            const closed = data.socketMessage || {};
            const current = this.state.incoming || {};
            if (!closed.sessionId || closed.sessionId === current.sessionId) {
                this.closePopup();
            }
        }
    }

    closePopup = () => {
        this.clearTimers();
        this.stopRing();
        this.setState({
            visible: false,
            incoming: null,
            countdown: 0,
            acting: false,
        });
    };

    handleCallingTimeout = async (auto) => {
        const incoming = this.state.incoming;
        if (!incoming) {
            this.closePopup();
            return;
        }
        if (this.state.acting) return;
        this.setState({ acting: true });
        try {
            await markVideoClaimCallingTimeout(incoming.sessionId);
        } catch (e) {}
        if (auto) {
            message.warn('视频呼入未接听，已自动挂断');
        }
        this.closePopup();
    };

    handleReject = async () => {
        const incoming = this.state.incoming;
        if (!incoming || this.state.acting) return;
        this.setState({ acting: true });
        try {
            const res = await rejectVideoClaim(incoming.sessionId);
            if (res && res.errCode === 0) {
                this.closePopup();
            } else {
                message.error((res && res.errInfo) || '拒接失败');
                this.setState({ acting: false });
            }
        } catch (e) {
            message.error('拒接失败');
            this.setState({ acting: false });
        }
    };

    handleAnswer = async () => {
        const incoming = this.state.incoming;
        if (!incoming || this.state.acting) return;
        this.setState({ acting: true });
        try {
            const res = await answerVideoClaim(incoming.sessionId);
            if (res && res.errCode === 0) {
                this.clearTimers();
                this.stopRing();
                // 跳转到工单详情页，带上 answer 返回的 session 数据，页面加载时直接渲染 VideoSurveyRoom
                this.props.history.push({
                    pathname: `/workBench/ReportDetail/${incoming.flowId}`,
                    state: { videoClaimSession: res.result },
                });
                this.closePopup();
            } else {
                message.error((res && res.errInfo) || '接听失败');
                this.setState({ acting: false });
            }
        } catch (e) {
            message.error('接听失败');
            this.setState({ acting: false });
        }
    };

    render() {
        const { visible, incoming, countdown, acting } = this.state;
        if (!visible) return null;
        const inviteeType = incoming && incoming.inviteeType;
        const typeLabel = INVITEE_TYPE_LABEL[inviteeType] || '客户';
        const inviteeName = (incoming && incoming.inviteeName) || '';
        const flowId = incoming && incoming.flowId;
        const min = String(Math.floor(countdown / 60)).padStart(2, '0');
        const sec = String(countdown % 60).padStart(2, '0');
        return (
            <Draggable
                handle=".vc-incoming-drag-handle"
                defaultPosition={{ x: 0, y: 0 }}
            >
                <div className="vc-incoming-popup">
                    <div className="vc-incoming-drag-handle">
                        <span>视频呼入</span>
                    </div>
                    <div className="vc-incoming-body">
                        <div className="vc-incoming-info">
                            <b>{typeLabel}</b>
                            {inviteeName ? <span style={{ marginLeft: 6 }}>{inviteeName}({typeLabel})</span> : null}
                            <span style={{ marginLeft: 6 }}>发起视频通话</span>
                        </div>
                        <div className="vc-incoming-flow">
                            受理号：{flowId || '-'}
                        </div>
                        <Spin spinning={acting} />
                        <div className="vc-incoming-countdown">
                            {countdown > 0 ? `未接听将在 ${min}:${sec} 后自动挂断` : '处理中…'}
                        </div>
                        <div className="vc-incoming-actions">
                            <Button danger loading={acting} onClick={this.handleReject}>拒接</Button>
                            <Button type="primary" loading={acting} onClick={this.handleAnswer}>
                                接听
                            </Button>
                        </div>
                    </div>
                </div>
            </Draggable>
        );
    }
}

export default withRouter(VideoClaimIncoming);
